(ns minorodata.sql-console.api
  (:require [mold-http.core :refer [reg-event-http]]
            [re-posh.core :as re-posh]
            [minorodata.app.config :refer [slow-query-duration-s]]
            [minorodata.app.utils :as app.utils]
            [minorodata.sql-console.model :refer [->sql-editor ->sql-query-history ->sql-query-favourites ->sql-query ->sql-metadata-partial]]))

;;
;; Fetch metadata incrementally
;;

(defn ->metadata-request-id [{:keys [object catalog schema table]}]
  (keyword (str object catalog schema table)))

(defn fetch-metadata-request [_ [params {:keys [on-success]}]]
  {:http-xhrio
   {:id        (->metadata-request-id params)
    :method    :post
    :url       "/api/sink-metadata"
    :params    (app.utils/clean-up-query params)
    :cb-params {:params     params
                :on-success on-success}}})


(defn fetch-metadata-callback [_ [error resp {:keys [params on-success]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch Warehouse metadata"]}
    (let [metadata (->sql-metadata-partial {:params   params
                                            :metadata resp})]
      (when on-success (on-success resp))
      {:dispatch [:sql-metadata-partial/add metadata]})))


(reg-event-http
 :sql-console.api/fetch-metadata
 fetch-metadata-request
 fetch-metadata-callback)


;;
;; SQL Query Runner
;;


(defn post-query-request [_ [{:keys [query max-rows environment-id]} cb-params]]
  {:http-xhrio
   {:id        :sql-query-post-request
    :method    :post
    :url       "/api/sink-console"
    :params    (cond-> {:query          query}
                 (some? max-rows) (assoc :max-rows max-rows)
                 environment-id (assoc :environment-id environment-id))
    :cb-params cb-params}
   :dispatch [:sql-editor/update (->sql-editor {:query-start-time (str (js/Date.))})]})


(defn get-response-signature
  "Gets headers of returned results. A vector of headers allows the user
  to distinguish if the request structure has changed or it's just
  the values that have been updated"
  [data]
  (map first data))


(defn post-query-callback [_ [error resp {:keys [on-success on-failure silent? skip-console?]}]]
  (let [current-tab-idx  @(re-posh/subscribe [:sql-editor/prop :sql-editor/current-tab])
        tdc              @(re-posh/subscribe [:sql-editor/prop :sql-editor/tab-data-container])
        tab-data         (nth tdc current-tab-idx {})
        current-metadata (:metadata tab-data)
        tab-id (get-in current-metadata [:tab-id] (str current-tab-idx))]
    (if (some? error)
      (let [error-message   (get-in error [:response :error])
            message         (or (get-in error [:response :message])
                                (get-in error [:response :error]))
            time-out-error? (= 0 (:status error))

            new-tab-data (-> tdc
                             (assoc-in [current-tab-idx :error-message] (if time-out-error?
                                                                          "Request timed out"
                                                                          error-message))
                             (assoc-in [current-tab-idx :results-tab] 0)
                             (assoc-in [current-tab-idx :failed?] true))]
        (when on-failure (on-failure))
        {:dispatch-n
         (when-not silent?
           [(when-not skip-console?
              [:sql-editor/update (->sql-editor {:tab-data-container new-tab-data})])

            (when-not skip-console?
              [:sql-console.events/set-tab-data tab-id []])

            (when time-out-error?
              [:app.events/add-snack :error "A long running SQL Console query has failed. The query timed out on the Warehouse."])

            [:app.api/log :error "Failed to execute query" error]

            ;; Update history so that even failed queries are displayed
            [:sql-console.events/reset-history]])})

      (let [data             (:results resp)
            query-duration   (:time-ms resp)

            current-data     @(re-posh/subscribe [:sql-console.data/data-for-tab tab-id])

            slow-query?      (> query-duration (* 1000 slow-query-duration-s))

            old-signature      (get-response-signature current-data)
            new-signature      (get-response-signature data)
            signature-changed? (not= old-signature new-signature)

            new-tab-data (-> tdc
                             (assoc-in [current-tab-idx :error-message] "")
                             (assoc-in [current-tab-idx :metadata] (dissoc current-metadata :transform-parsing-error))
                             (assoc-in [current-tab-idx :failed?] false)
                             (assoc-in [current-tab-idx :query-duration] query-duration))]

        (when on-success (on-success {:data data}))

        {:dispatch-n [(when-not skip-console?
                        [:sql-editor/update
                         (->sql-editor
                         {:tab-data-container new-tab-data
                          :results-tab-configs {}
                          :results-tab (dec (count data))})])

                      (when-not skip-console?
                        [:sql-console.events/set-tab-data tab-id data])

                      (when slow-query?
                        [:app.events/add-snack :success "A long running SQL Console query has completed"])

                      [:sql-console.events/reset-history]]}))))


(reg-event-http
  :sql-console.api/post-query
  post-query-request
  post-query-callback)


;;
;; SQL Query Validation
;;


(def QUERY_VALIDATION_DELAY 3000)

(defn validate-query-request [_ [query]]
  {:http-xhrio
   {:id     :sql-query-validate-request
    :method :post
    :url    "/api/sink-console"
    :params {:query        (str "EXPLAIN " query)
             :skip-history true}}})


(defn validate-query-callback [_ [error]]
  (if (some? error)
    {:dispatch-n     [[:app.events/add-snack :error "Failed to validate query"]
                      [:sql-editor/update (->sql-editor {:selection-validity :invalid})]]
     :dispatch-later [{:ms       QUERY_VALIDATION_DELAY
                       :dispatch [:sql-editor/update (->sql-editor {:selection-validity :unknown})]}]}

    {:dispatch-n     [[:sql-editor/update (->sql-editor {:selection-validity :valid})]
                      [:app.events/add-snack :success "Selection is valid"]]
     :dispatch-later [{:ms       QUERY_VALIDATION_DELAY
                       :dispatch [:sql-editor/update (->sql-editor {:selection-validity :unknown})]}]}))


(reg-event-http
  :sql-console.api/validate-query
  validate-query-request
  validate-query-callback)


;;
;; SQL History
;;


(defn generate-updated-state-with-pagination [{:keys [response replace? key-rename-fn latest-order-index]}]
  (let [{:keys [results pagination sort]} response
        results-count (count results)
        [start-idx end-idx] (if replace?
                              [0 results-count]
                              [latest-order-index (+ latest-order-index results-count)])
        queries       (mapv (fn [idx query] (assoc query :order idx))
                            (range start-idx end-idx)
                            results)]
    (-> {:queries            queries
         :latest-order-index end-idx}
        (merge pagination sort)
        key-rename-fn)))


(defn fetch-query-history-params [overrides stored-query-params]
  (let [{:sql-query-history/keys [page order-by order-dir items-per-page]} stored-query-params
        sort (cond-> nil
                     (some? order-by) (assoc :order-by order-by)
                     (some? order-dir) (assoc :order-dir order-dir)
                     (some? (:order-by overrides)) (assoc :order-by (:order-by overrides))
                     (some? (:order-dir overrides)) (assoc :order-dir (:order-dir overrides)))]
    (app.utils/assoc-some {:pagination {:page           (or (:page overrides) page 1)
                                        :items-per-page (or (:items-per-page overrides) items-per-page 100)}}
      :sort sort)))

(defn fetch-history-request [_ [{:keys [page replace?]
                                 :as   params
                                 :or   {replace? false}}]]
  (let [state @(re-posh/subscribe [:sql-query-history/pull])]
    (when (or (nil? page)
              (<= page (:sql-query-history/total-pages state)))
      {:http-xhrio
       {:id        :fetch-history-request
        :strategy  :keep-latest
        :url       "/api/sink-console/history"
        :params    (fetch-query-history-params params state)
        :cb-params replace?}})))


(defn fetch-history-callback [_ [error resp replace?]]
  (if (some? error)
    {:dispatch-n
     [[:app.events/add-snack :error "Failed to fetch query history"]
      [:app.api/log :error "Failed to fetch query history" error]]}

    (let [{:sql-query-history/keys [latest-order-index]} @(re-posh/subscribe [:sql-query-history/pull])
          query-history (generate-updated-state-with-pagination
                          {:key-rename-fn      ->sql-query-history
                           :replace?           replace?
                           :response           resp
                           :latest-order-index latest-order-index})]

      {:dispatch [:sql-query-history/update query-history {:replace replace?}]})))

(reg-event-http
  :sql-console.api/fetch-history
  fetch-history-request
  fetch-history-callback)


;;
;; SQL History Favourites
;;


(defn fetch-query-favourites-params [overrides stored-query-params]
  (let [{:sql-query-favourites/keys [page order-by order-dir items-per-page]} stored-query-params
        sort (cond-> nil
                     (some? order-by) (assoc :order-by order-by)
                     (some? order-dir) (assoc :order-dir order-dir)
                     (some? (:order-by overrides)) (assoc :order-by (:order-by overrides))
                     (some? (:order-dir overrides)) (assoc :order-dir (:order-dir overrides)))]
    (app.utils/assoc-some {:pagination {:page           (or (:page overrides) page 1)
                                        :items-per-page (or (:items-per-page overrides) items-per-page 100)}}
      :sort sort)))


(defn fetch-favourites-request [_ [{:keys [page replace?]
                                    :as   params
                                    :or   {replace? false}}]]
  (let [state @(re-posh/subscribe [:sql-query-favourites/pull])]
    (when (or (nil? page)
              (<= page (:sql-query-favourites/total-pages state)))
      {:http-xhrio
       {:id        :fetch-favourites-request
        :strategy  :keep-latest
        :url       "/api/sink-console/favourites"
        :params    (fetch-query-favourites-params params state)
        :cb-params replace?}})))


(defn fetch-favourites-callback [_ [error resp replace?]]
  (if (some? error)
    {:dispatch-n
     [[:app.events/add-snack :error "Failed to fetch favourites"]
      [:app.api/log :error "Failed to fetch favourites" error]]}

    (let [{:sql-query-favourites/keys [latest-order-index]} @(re-posh/subscribe [:sql-query-favourites/pull])

          query-history (generate-updated-state-with-pagination
                          {:key-rename-fn      ->sql-query-favourites
                           :replace?           replace?
                           :response           resp
                           :latest-order-index latest-order-index})]

      {:dispatch [:sql-query-favourites/update query-history {:replace replace?}]})))


(reg-event-http
  :sql-console.api/fetch-favourites
  fetch-favourites-request
  fetch-favourites-callback)


;;
;; Favourite/unfavourite a query
;;


(defn favourite-query-request [_ [db-id sql-query-id favourite]]
  {:http-xhrio
   {:id        :sql-query-favourite-request
    :method    :put
    :url       (str "/api/sink-console/" sql-query-id)
    :params    {:favourite favourite}
    :cb-params {:id           db-id
                :sql-query-id sql-query-id
                :favourite    favourite}}})


(defn favourite-query-callback [_ [error _ {:keys [id favourite]}]]
  (if (some? error)
    {:dispatch-n
     [[:app.events/add-snack :error "Failed to favourite the query"]
      [:app.api/log :error "Failed to favourite the query" error]]}

    {:dispatch-n [[:sql-query/update id {:sql-query/favourite favourite}]
                  [:sql-console.api/fetch-favourites {:page      nil
                                                      :order-by  "created_at"
                                                      :order-dir "desc"
                                                      :replace?  true}]]}))


(reg-event-http
  :sql-console.api/favourite-query
  favourite-query-request
  favourite-query-callback)


;;
;; Edit a query
;;


(defn edit-query-request [_ [db-id sql-query-id params]]
  {:http-xhrio
   {:id        :sql-query-edit-request
    :method    :post
    :url       (str "/api/sink-console/history/" sql-query-id)
    :params    {:name (:name params)}
    :cb-params {:sql-query-id sql-query-id
                :params       params
                :db-id        db-id}}})


(defn edit-query-callback [_ [error resp {:keys [db-id]}]]
  (if (some? error)
    {:dispatch-n
     [[:app.events/add-snack :error "Failed to edit the query"]
      [:app.api/log :error "Failed to edit the query" error]]}

    {:dispatch-n [[:sql-query/update db-id (->sql-query (get-in resp [:result 0]))]]}))


(reg-event-http
  :sql-console.api/edit-sql-query
  edit-query-request
  edit-query-callback)
